/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/types/organization.ts
 * PURPOSE: TypeScript types for Organization Management per ADR-028
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * DATE: 2026-01-19
 * DESIGN: docs/02-design/04-ui-ux-design/ORGANIZATION-MANAGEMENT-UI-DESIGN.md
 * APPROVED: Sprint 27 - Pilot Onboarding
 * COMPLIANCE: SDLC 6.3.1 + ADR-028 Organization Entity Schema
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management - Organization Hierarchy
 * - Impact: Type safety for 8-Layer hierarchy CRUD operations
 * - Stakeholders: Frontend Team, Backend Team
 *
 * @technical-architecture
 * - Layer: Type Definitions
 * - Pattern: ADR-028 21-column schema mapping
 * - Dependencies: TypeScript 5.0+
 *
 * @cross-references
 * - ADR: docs/02-design/18-adrs/ADR-028-organization-entity-schema-standardization.md
 * - API: backend/app/api/v1/organization.py
 * - Design: docs/02-design/04-ui-ux-design/ORGANIZATION-MANAGEMENT-UI-DESIGN.md
 */

/**
 * 8-Layer Vietnamese F&B Hierarchy Types
 * Per ADR-028 and 8-LAYER-VIETNAMESE-HIERARCHY.md
 */
export type OrganizationType =
  | 'corporation'  // L1: Tập đoàn
  | 'chain'        // L2: Chuỗi
  | 'brand'        // L3: Thương hiệu
  | 'region'       // L4: Vùng
  | 'branch'       // L5: Chi nhánh
  | 'work_area'    // L6: Khu vực làm việc
  | 'shift'        // L7: Ca làm việc
  | 'team';        // L8: Đội nhóm

/**
 * Organization Type Display Configuration
 * Vietnamese labels and visual representation
 */
export const ORGANIZATION_TYPE_CONFIG: Record<OrganizationType, {
  label: string;
  labelVi: string;
  icon: string;
  level: number;
  colorClass: string;
}> = {
  corporation: {
    label: 'Corporation',
    labelVi: 'Tập đoàn',
    icon: '🏛️',
    level: 1,
    colorClass: 'bg-amber-100 text-amber-800',
  },
  chain: {
    label: 'Chain',
    labelVi: 'Chuỗi',
    icon: '🔗',
    level: 2,
    colorClass: 'bg-amber-50 text-amber-700',
  },
  brand: {
    label: 'Brand',
    labelVi: 'Thương hiệu',
    icon: '🏷️',
    level: 3,
    colorClass: 'bg-green-100 text-green-800',
  },
  region: {
    label: 'Region',
    labelVi: 'Vùng',
    icon: '🗺️',
    level: 4,
    colorClass: 'bg-green-50 text-green-700',
  },
  branch: {
    label: 'Branch',
    labelVi: 'Chi nhánh',
    icon: '🏪',
    level: 5,
    colorClass: 'bg-yellow-100 text-yellow-800',
  },
  work_area: {
    label: 'Work Area',
    labelVi: 'Khu vực làm việc',
    icon: '📍',
    level: 6,
    colorClass: 'bg-yellow-50 text-yellow-700',
  },
  shift: {
    label: 'Shift',
    labelVi: 'Ca làm việc',
    icon: '⏰',
    level: 7,
    colorClass: 'bg-gray-100 text-gray-700',
  },
  team: {
    label: 'Team',
    labelVi: 'Đội nhóm',
    icon: '👥',
    level: 8,
    colorClass: 'bg-gray-50 text-gray-600',
  },
};

/**
 * DD-0027: Entity Type — pháp nhân classification per MDG-004
 * Design-First Gate: DD-0027 v1.2 §3.4
 */
export type EntityType =
  | 'holding'            // Tập đoàn (NQH)
  | 'operating_company'  // Công ty vận hành (NQDL)
  | 'profit_center'      // Trung tâm lợi nhuận (5 PCs)
  | 'cost_center'        // Trung tâm chi phí
  | 'franchise';         // Nhượng quyền (AirDream 2026)

/**
 * DD-0027: Business Unit — ngành kinh doanh per MDG-004
 * Design-First Gate: DD-0027 v1.2 §3.4
 */
export type BusinessUnit =
  | 'food_beverage'  // THOM, BKL, AIRDREAM
  | 'hospitality'    // KUPID, TMM
  | 'retail'
  | 'services';

export const ENTITY_TYPE_OPTIONS: { value: EntityType; label: string; labelVi: string }[] = [
  { value: 'holding', label: 'Holding', labelVi: 'Tập đoàn / Holding' },
  { value: 'operating_company', label: 'Operating Company', labelVi: 'Công ty Vận hành' },
  { value: 'profit_center', label: 'Profit Center', labelVi: 'Trung tâm Lợi nhuận' },
  { value: 'cost_center', label: 'Cost Center', labelVi: 'Trung tâm Chi phí' },
  { value: 'franchise', label: 'Franchise', labelVi: 'Nhượng quyền' },
];

export const BUSINESS_UNIT_OPTIONS: { value: BusinessUnit; label: string; labelVi: string }[] = [
  { value: 'food_beverage', label: 'Food & Beverage', labelVi: 'Ăn uống (F&B)' },
  { value: 'hospitality', label: 'Hospitality', labelVi: 'Du lịch lưu trú' },
  { value: 'retail', label: 'Retail', labelVi: 'Bán lẻ' },
  { value: 'services', label: 'Services', labelVi: 'Dịch vụ' },
];

/**
 * Default config for null/undefined organization_type
 * Used when data from database has organization_type = null
 */
export const DEFAULT_ORGANIZATION_TYPE_CONFIG = {
  label: 'Unknown',
  labelVi: 'Chưa xác định',
  icon: '❓',
  level: 0,
  colorClass: 'bg-gray-200 text-gray-500',
};

/**
 * Safe getter for organization type config
 * Returns default config if type is null/undefined or unknown.
 *
 * DD-0052 fix-cycle Issue #3: case-insensitive lookup so legacy DB rows
 * with uppercase organization_type (e.g., "BRANCH") still resolve to the
 * lowercase TYPE_CONFIG entries.
 */
export function getOrganizationTypeConfig(type: OrganizationType | string | null | undefined) {
  if (!type) {
    return DEFAULT_ORGANIZATION_TYPE_CONFIG;
  }
  const normalized = String(type).toLowerCase() as OrganizationType;
  const config = ORGANIZATION_TYPE_CONFIG[normalized];
  if (!config) {
    return DEFAULT_ORGANIZATION_TYPE_CONFIG;
  }
  return config;
}

/**
 * Organization Status
 */
export type OrganizationStatus = 'active' | 'inactive' | 'pending';

export const ORGANIZATION_STATUS_CONFIG: Record<OrganizationStatus, {
  label: string;
  labelVi: string;
  icon: string;
  colorClass: string;
}> = {
  active: {
    label: 'Active',
    labelVi: 'Hoạt động',
    icon: '✅',
    colorClass: 'bg-green-100 text-green-800',
  },
  inactive: {
    label: 'Inactive',
    labelVi: 'Ngừng hoạt động',
    icon: '⏸️',
    colorClass: 'bg-gray-100 text-gray-600',
  },
  pending: {
    label: 'Pending',
    labelVi: 'Chờ duyệt',
    icon: '⏳',
    colorClass: 'bg-yellow-100 text-yellow-800',
  },
};

/**
 * Organization Entity - Per ADR-028 Target Schema
 * SSOT: docs/02-design/18-adrs/ADR-028-organization-entity-schema-standardization.md
 */
export interface OrganizationEntity {
  // Primary identifiers
  id: string;
  code: string;
  name: string;

  // 8-Layer Hierarchy fields
  organization_type: OrganizationType;
  level: number;                    // Note: NOT hierarchy_level
  parent_id: string | null;
  tenant_key: string;
  path?: string;

  // Contact Information (single address field per ADR-028)
  address?: string;                 // Full address in one field
  phone?: string;
  email?: string;

  // Vietnamese Business Registration (ADR-028 field names)
  tax_code?: string;                // Note: NOT tax_id
  name_vietnamese?: string;
  business_license_no?: string;     // Note: NOT business_license
  legal_representative?: string;
  registered_capital?: number;

  // DD-0027: MDG-004 Business Classification (SPEC-0413 FR-006)
  entity_type?: EntityType | null;
  business_unit?: BusinessUnit | null;
  gl_segment?: string | null;
  is_profit_center?: boolean;
  org_manager_id?: string | null;

  // AI Optimization (ADR-028)
  ai_enabled?: boolean;
  solver_priority?: number;

  // Status
  is_active: boolean;

  // Response-only fields (NOT in create/update per ADR-028)
  // Note: business_type REMOVED per PM/PJM decision (2026-01-20)
  // See: docs/06-deploy/12-Production-Pilot/BUSINESS-TYPE-EMPLOYEE-COUNT-ANALYSIS-REPORT.md
  timezone?: string;
  currency?: string;
  website?: string;
  industry?: string | null;
  settings?: Record<string, unknown>;

  // Metadata
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

/**
 * Organization Create DTO - Per ADR-028 OrganizationCreate Schema
 * Only includes fields accepted by POST /api/v1/organizations/
 * SSOT: docs/02-design/18-adrs/ADR-028-organization-entity-schema-standardization.md
 */
export interface OrganizationCreate {
  // Core Identity (Required)
  code: string;
  name: string;

  // 8-Layer Hierarchy (Optional per ADR-028)
  organization_type?: OrganizationType;
  level?: number;                     // Note: NOT hierarchy_level
  parent_id?: string | null;

  // DD-0102 §4 (PROPOSED — triển khai theo chỉ đạo trực tiếp user 2026-08-25,
  // DD chưa được @cto ratify chính thức): id thật của unit_types đã chọn —
  // ưu tiên hơn suy ngược organization_type ở backend.
  unit_type_id?: string | null;

  // Contact Information (Optional)
  address?: string;                   // Single field - full address
  phone?: string;
  email?: string;

  // Vietnamese Business Registration (Optional)
  tax_code?: string;                  // Note: NOT tax_id
  name_vietnamese?: string;
  business_license_no?: string;       // Note: NOT business_license
  legal_representative?: string;
  registered_capital?: number;

  // DD-0027: MDG-004 Business Classification (Optional, SPEC-0413 FR-006)
  entity_type?: EntityType | null;
  business_unit?: BusinessUnit | null;
  gl_segment?: string | null;
  is_profit_center?: boolean;
  org_manager_id?: string | null;

  // AI Optimization (Optional)
  ai_enabled?: boolean;
  solver_priority?: number;
}

/**
 * Note: The following fields are RESPONSE-ONLY per ADR-028
 * (NOT accepted in create/update requests):
 * - timezone (defaults to Asia/Ho_Chi_Minh)
 * - currency (defaults to VND)
 * - max_employees (computed by backend)
 * - settings
 * - website
 * - industry
 *
 * REMOVED per PM/PJM decision (2026-01-20):
 * - business_type (was descriptive only, not a Stage 01 requirement)
 * See: docs/06-deploy/12-Production-Pilot/BUSINESS-TYPE-EMPLOYEE-COUNT-ANALYSIS-REPORT.md
 */

/**
 * Organization Update DTO - Per ADR-028 OrganizationUpdate Schema
 * All fields optional for PATCH-style updates
 */
export interface OrganizationUpdate extends Partial<OrganizationCreate> {
  is_active?: boolean;
}

/**
 * Organization List Filters - Per ADR-028
 */
export interface OrganizationFilters {
  organization_type?: OrganizationType;
  level?: number;                     // Note: NOT hierarchy_level
  is_active?: boolean;
  parent_id?: string;
  search?: string;
  skip?: number;
  limit?: number;
  // DD-0027: Business Classification filters (SPEC-0413 FR-007, organizations.yaml v2.1.0)
  entity_type?: EntityType;
  business_unit?: BusinessUnit;
  gl_segment?: string;
  is_profit_center?: boolean;
}

/**
 * Organization Tree Node for hierarchy view
 */
export interface OrganizationTreeNode extends OrganizationEntity {
  children: OrganizationTreeNode[];
  depth: number;
  isExpanded?: boolean;
}

/**
 * Form validation error messages (Vietnamese) - Per ADR-028 field names
 */
export const ORGANIZATION_VALIDATION_MESSAGES = {
  code: {
    required: 'Mã tổ chức là bắt buộc',
    pattern: 'Mã chỉ chứa chữ IN HOA, số, dấu gạch ngang',
    maxLength: 'Mã không được quá 50 ký tự',
    unique: 'Mã tổ chức đã tồn tại trong hệ thống',
  },
  name: {
    required: 'Tên tổ chức là bắt buộc',
    maxLength: 'Tên không được quá 255 ký tự',
  },
  organization_type: {
    enum: 'Loại hình không hợp lệ',
  },
  level: {
    min: 'Cấp độ tối thiểu là 1',
    max: 'Cấp độ tối đa là 8',
  },
  email: {
    pattern: 'Email không hợp lệ',
  },
  phone: {
    pattern: 'Số điện thoại phải theo định dạng +84xxxxxxxxx hoặc 0xxxxxxxxx',
  },
  tax_code: {  // Note: ADR-028 uses tax_code, NOT tax_id
    pattern: 'Mã số thuế phải có đúng 10 chữ số',
  },
  business_license_no: {  // Note: ADR-028 uses business_license_no
    maxLength: 'Số GPKD không được quá 50 ký tự',
  },
  solver_priority: {
    min: 'Độ ưu tiên tối thiểu là 1',
    max: 'Độ ưu tiên tối đa là 100',
  },
  parent_id: {
    invalid_hierarchy: 'Tổ chức cha phải có cấp độ thấp hơn',
  },
  // DD-0027: Business Classification validation messages
  entity_type: {
    enum: 'Loại pháp nhân không hợp lệ',
  },
  business_unit: {
    enum: 'Ngành kinh doanh không hợp lệ',
  },
  gl_segment: {
    maxLength: 'Mã GL không được quá 10 ký tự',
  },
};

/**
 * Helper function to get hierarchy level from type
 *
 * Defensive lookup (DD-0052 fix-cycle Issue #3): legacy DB rows may contain
 * uppercase organization_type values (e.g., "BRANCH") that don't match the
 * lowercase enum keys in ORGANIZATION_TYPE_CONFIG. Without normalization,
 * `ORGANIZATION_TYPE_CONFIG[type]` returns undefined and `.level` crashes
 * with "Cannot read properties of undefined (reading 'level')".
 *
 * Strategy: case-insensitive lookup → fall back to DEFAULT_ORGANIZATION_TYPE_CONFIG.level
 * (= 0) if still unmatched. Caller code can treat 0 as "unknown" sentinel.
 *
 * Phase-out (per phase-out policy — keep prior impl as comment for audit trail):
 * // Old impl crashed on legacy uppercase types:
 * // export function getHierarchyLevel(type: OrganizationType): number {
 * //   return ORGANIZATION_TYPE_CONFIG[type].level;
 * // }
 */
export function getHierarchyLevel(type: OrganizationType | string | null | undefined): number {
  if (!type) return DEFAULT_ORGANIZATION_TYPE_CONFIG.level;
  const normalized = String(type).toLowerCase() as OrganizationType;
  const config = ORGANIZATION_TYPE_CONFIG[normalized];
  if (!config) return DEFAULT_ORGANIZATION_TYPE_CONFIG.level;
  return config.level;
}

/**
 * Helper function to generate tenant_key from code
 */
export function generateTenantKey(code: string): string {
  return `TENANT-${code.toUpperCase()}`;
}

/**
 * Helper function to validate parent-child relationship
 * Parent level must be less than child level.
 *
 * DD-0052 fix-cycle Issue #3: relax param type to accept legacy uppercase
 * variants — getHierarchyLevel now normalizes case internally and returns
 * 0 for unknown types (defensive).
 */
export function isValidParentChild(
  parentType: OrganizationType | string | null | undefined,
  childType: OrganizationType | string | null | undefined
): boolean {
  if (!parentType) return true; // Top-level organizations have no parent
  const parentLevel = getHierarchyLevel(parentType);
  const childLevel = getHierarchyLevel(childType);
  // If either is unknown (level 0), be permissive — let backend validate.
  if (parentLevel === 0 || childLevel === 0) return true;
  return parentLevel < childLevel;
}
